/**
 * Sélecteur de tenant — affiché dans la Topbar quand l'utilisateur
 * appartient à plusieurs domaines (cas comptable externe, œnologue
 * conseil, ou propriétaire avec plusieurs structures).
 *
 * Comportement :
 *   - Si 1 seul tenant : rendu en texte simple "· Champagne X" (pas de
 *     menu cliquable, on évite de polluer l'UI quand y a rien à choisir).
 *   - Si 2+ tenants : bouton chevron qui ouvre une liste. Au clic sur un
 *     autre tenant → ouverture de la modale `<SwitchTenantConfirmDialog>`
 *     qui exige mdp + TOTP avant de déclencher le handoff (cf
 *     docs/multitenancy.md § Switch tenant). Cette ré-authentification
 *     bloque le scénario « tiers sur la machine de Marie qui clique
 *     silencieusement sur un autre client ».
 *
 *   La validation modale appelle `auth.createHandoffWithReauth` puis
 *   `window.location.assign(handoff_url)` : le navigateur change physiquement
 *   de host (`<slug>.crayea.fr`) et la nouvelle session ERP est posée par
 *   la page handoff sur le bon sous-domaine. Aucun cache React Query à
 *   purger côté tenant courant puisque le browser quitte ce host.
 *
 * Le menu se ferme si on clique en dehors (handler global sur document).
 */

import { Building2, Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { SwitchTenantConfirmDialog } from "./SwitchTenantConfirmDialog.js";

export interface TenantSwitcherTenant {
  id: string;
  raison_sociale: string;
  /** Code rôle de l'utilisateur sur ce tenant (utile pour info contextuelle). */
  role_label?: string;
  /** Vrai si le domaine est en `en_attente_validation` (impossible d'y switcher). */
  pending_validation?: boolean;
}

export interface TenantSwitcherProps {
  tenants: ReadonlyArray<TenantSwitcherTenant>;
  activeId: string | null;
}

export function TenantSwitcher({ tenants, activeId }: TenantSwitcherProps) {
  const [open, setOpen] = useState(false);
  // Tenant cible en attente de confirmation (modale ouverte). Null = pas de modale.
  const [pendingTarget, setPendingTarget] = useState<TenantSwitcherTenant | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const active = tenants.find((t) => t.id === activeId) ?? tenants[0] ?? null;

  // Ferme le menu si on clique en dehors. On ignore ce handler quand la modale
  // de confirmation est ouverte (la modale gère sa propre fermeture).
  useEffect(() => {
    if (!open || pendingTarget) return;
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, pendingTarget]);

  // Cas dégénéré : 0 ou 1 tenant → pas de menu, juste un libellé statique.
  if (tenants.length <= 1) {
    return (
      <span className="eyebrow block min-w-0 truncate" title={active?.raison_sociale}>
        {/* Le « · » separe la marque du domaine : masque avec elle sous `lg`,
            sinon il pend tout seul en tete de barre. */}
        <span className="hidden lg:inline">·&nbsp;</span>
        {active?.raison_sociale ?? "Aucun domaine"}
      </span>
    );
  }

  // Au clic sur un tenant ≠ actif : on ferme le dropdown et on ouvre la modale
  // de ré-authentification. Le switch ne se déclenchera qu'après mdp + TOTP OK.
  const handleTenantClick = (tenant: TenantSwitcherTenant) => {
    setOpen(false);
    setPendingTarget(tenant);
  };

  return (
    <>
      <div ref={menuRef} className="relative min-w-0 max-w-full">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="inline-flex min-h-12 max-w-full items-center gap-1.5 rounded-md px-2 py-1 transition hover:bg-champagne-900/[0.06]"
        >
          <span className="eyebrow min-w-0 truncate" title={active?.raison_sociale}>
            <span className="hidden lg:inline">·&nbsp;</span>
            {active?.raison_sociale ?? "Choisir un domaine"}
          </span>
          <ChevronDown
            size={14}
            className={`shrink-0 text-champagne-700 transition ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div
            role="listbox"
            className="absolute left-0 top-full z-50 mt-1.5 w-72 max-w-[calc(100vw-6rem)] overflow-hidden rounded-lg border border-champagne-300 bg-white shadow-lg"
          >
            <div className="border-b border-neutral-100 bg-neutral-50 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
              Vos domaines ({tenants.length})
            </div>
            <ul className="max-h-80 overflow-y-auto py-1">
              {tenants.map((t) => {
                const isActive = t.id === activeId;
                const isPending = t.pending_validation === true;
                const isDisabled = isActive || isPending;
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      disabled={isDisabled}
                      onClick={() => handleTenantClick(t)}
                      className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition ${
                        isActive
                          ? "cursor-default bg-champagne-50 text-aubergine-900"
                          : isPending
                            ? "cursor-not-allowed opacity-60"
                            : "hover:bg-neutral-50"
                      }`}
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-champagne-100 text-champagne-700">
                        <Building2 size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-aubergine-900">
                          {t.raison_sociale}
                        </div>
                        <div className="truncate text-xs text-neutral-500">
                          {isPending ? "En cours de validation" : t.role_label}
                        </div>
                      </div>
                      {isActive && <Check size={16} className="shrink-0 text-champagne-700" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {pendingTarget && (
        <SwitchTenantConfirmDialog
          targetDomaineId={pendingTarget.id}
          targetRaisonSociale={pendingTarget.raison_sociale}
          onCancel={() => setPendingTarget(null)}
        />
      )}
    </>
  );
}
